import { getHostName } from '@/app/utils';
import { HOST_NAMES } from '@/app/utils/constants';

export const Tooltips = {
  TRAIN_TEST_VALIDATION_SPLIT:
    'Train, validate and test split that will be employed while training. Note that the number of images shown for each split is approximate, and real values may change.',
  NUMBER_OF_EPOCHS:
    'Epochs denote the number of times your model will go through a training dataset.',
  IMAGE_RESOLUTION:
    'Image Resolution that will be used while training. Images will be resized to this size before giving input to the model.',
  CROP_TRAINING_IMAGE:
    'Returns a rectangular region from the image. The parameters are integers defining the left, upper, right, and lower pixel coordinate.',
  BATCH_SIZE:
    'Number of samples processed together before the model is updated. Note that higher batch size may lead to memory problems.',
  EARLY_STOPPING_TOLERANCE:
    'This denotes the number of epochs that can be tolerated where the metric decreases in the validation set before stopping the training. A value of 0 means training stops as soon as the metric decreases in the validation set. To disable early stopping, set it to the number of epochs.',
  EARLY_STOPPING_MINIMUM_IMPROVEMENT:
    'Minimum increase in the validation metric value, which can be considered as an improvement. Setting this value to 0 will save the model if the validation metric increases by very small value. Assume that the metric is always between 0 and 1.',
  RANDOM_HORIZONTAL_FLIP_PROBABILITY:
    'Random Horizontal Flip Augmentation is applied to the training dataset. p denotes the probability of applying the horizontal flip.',
  RANDOM_VERTICAL_FLIP_PROBABILITY:
    'Random Vertical Flip Augmentation is applied to the training dataset. p denotes the probability of applying the vertical flip.',
  RANDOM_ROTATION_PROBABILITY:
    'Random Rotation is applied to the training dataset. p denotes the probability that the image will be rotated, and max rotation denotes the maximum angle in degrees by which the image will be rotated (can be both clockwise and anti-clockwise). It is possible that it will be rotated by less than max rotation.',
  RANDOM_TRANSLATION_PROBABILITY:
    'Random Translation is applied to the training dataset. p denotes the probability that the image will be translated, and height denotes the relative height ratio by which the image will be translated and similarly width  denotes the relative width ratio by which the image will be translated.',
  RANDOM_IMAGE_TONE_PROBABILITY:
    'Random Image Tone is applied to the training dataset. p denotes the probability that the image will be translated, and other properties denote maximum ratio for that property by which the image will be translated.',
  OPTIMIZER:
    'Optimization algorithm used for minimising the loss function. If SGD is selected, then it is implemented with momentum.',
  LEARNING_RATE_SCHEDULER:
    'Learning rate scheduler used while training. These are designed in such a way that the learning rate will be zero at the end of training. Linear Scheduler will be linear decay from max learning rate to zero. The Cosine Scheduler will apply quarter cycle cosine. The constant scheduler will apply a constant learning rate over the training.',
  WARMUP_EPOCHS:
    'It denotes the number of epochs at the start of training over which the learning rate increases from 0 to max value. A value of 0 denotes that there is no warmup. If enabled, always linear warmup will always be implemented.',
  MAXIMUM_LEARNING_RATE:
    'Maximum learning over the training schedule. It is used by optimisers to update the model weights. Maximum value of 2.0 if kept intentionally.',
  WEIGHT_DECAY:
    'Weight decay is the L2 regularisation applied to the weights at the time of training.',
  LOSS_FUNCTION:
    'Loss function to be used while training the model. For focal loss, gamma = 2.0 is set by default.',
  LABEL_SMOOTHING:
    'Specify the amount of smoothing when computing the loss where 0.0 means no smoothing. The target becomes a mixture of the original ground truth with uniform distribution.',
  CLASSWISE_WEIGHTAGE:
    'A manual rescaling weight given to each class where 1.0 means no rescaling.',
  MONITORED_METRIC:
    'Metric that will be used for model checkpoints and early stopping.',
  MONITORED_METRIC_CLASSES:
    'Subset of defects on which metric should be calculated and monitored. By default, all the model defects will be selected',
  TRAIN_MODE:
    'To enable use case specific pre-training, 0 is fine tuning, 1 is pre-training',
  EMA: 'To enable Teacher-Student training, 0 disable EMA, EMA mode 1,2,3',
  NO_DEFECT_CLASSES:
    'Classes used to mark a image as no defect. By default, no the model defects will be selected',
  NO_DEFECT_ZERO_HOT_ENCODING:
    'Enable zero hot encoding for the images marked as no defect.',
  JSON_INPUT_FIELD: 'JSON input field',
};

export const NumericInputProps = {
  type: 'number',
  InputProps: { inputProps: { min: 0 } },
};

const CommonDetectionImageResolutions = {
  64: {
    name: '64',
    value: 64,
  },
  128: {
    name: '128',
    value: 128,
  },
  256: {
    name: '256',
    value: 256,
  },
  320: {
    name: '320',
    value: 320,
  },
  480: {
    name: '480',
    value: 480,
  },
  576: {
    name: '576',
    value: 576,
  },
  640: {
    name: '640',
    value: 640,
  },
  768: {
    name: '768',
    value: 768,
  },
  1024: {
    name: '1024',
    value: 1024,
  },
  1280: {
    name: '1280',
    value: 1280,
  },
  1536: {
    name: '1536',
    value: 1536,
  },
  1920: {
    name: '1920',
    value: 1920,
  },
  2560: {
    name: '2560',
    value: 2560,
  },
  3200: {
    name: '3200',
    value: 3200,
  },
  3840: {
    name: '3840',
    value: 3840,
  },
  4096: {
    name: '4096',
    value: 4096,
  },
  0: {
    name: 'Custom',
    value: 0,
  },
};

const DetectionImageResolutions = {
  vit: { ...CommonDetectionImageResolutions },
  cnn: { ...CommonDetectionImageResolutions },
};

const ClassificationImageResolutions = {
  vit: {
    224: {
      name: '224',
      value: 224,
    },
    448: {
      name: '448',
      value: 448,
    },
    518: {
      name: '518',
      value: 518,
    },
    560: {
      name: '560',
      value: 560,
    },
    0: {
      name: 'Custom',
      value: 0,
    },
  },
  cnn: {
    64: {
      name: '64',
      value: 64,
    },
    128: {
      name: '128',
      value: 128,
    },
    256: {
      name: '256',
      value: 256,
    },
    320: {
      name: '320',
      value: 320,
    },
    480: {
      name: '480',
      value: 480,
    },
    576: {
      name: '576',
      value: 576,
    },
    0: {
      name: 'Custom',
      value: 0,
    },
  },
};

export const ImageResolutions = {
  CLASSIFICATION: ClassificationImageResolutions,
  CLASSIFICATION_AND_DETECTION: DetectionImageResolutions,
};

const getCNNTrainingDefaultConfigs = () => {
  const hostName = getHostName();

  switch (hostName) {
    case HOST_NAMES.GF7NONPROD:
    case HOST_NAMES.GF7PROD:
      return {
        no_of_epochs: 30,
        image_resolution: 480,
        train_test_validation_split: { train: 80, validation: 10, test: 10 },
      };
    default:
      return {
        no_of_epochs: 30,
        image_resolution: 256,
        train_test_validation_split: { train: 80, validation: 10, test: 10 },
      };
  }
};

const getVITTrainingDefaultConfigs = () => {
  const hostName = getHostName();

  switch (hostName) {
    case HOST_NAMES.TOWERSEMI:
    case HOST_NAMES.TOWERTEXAS:
      return {
        no_of_epochs: 30,
        image_resolution: 672,
        train_test_validation_split: { train: 75, validation: 20, test: 5 },
      };
    default:
      return {
        no_of_epochs: 15,
        image_resolution: 518,
        train_test_validation_split: { train: 80, validation: 10, test: 10 },
      };
  }
};

export const DefaultTrainingConfigs = {
  cnn: getCNNTrainingDefaultConfigs,
  vit: getVITTrainingDefaultConfigs,
};

export const OPTIMIZER_OPTIONS = [
  { name: 'Adam', value: 'adam' },
  { name: 'AdamW', value: 'adamw' },
  { name: 'SGD', value: 'sgd' },
];

export const LOSS_FUNCTION_OPTIONS = [
  { name: 'Cross Entropy Loss', value: 'cross_entropy_loss' },
  { name: 'Focal Loss', value: 'focal_loss' },
];

export const CROP_TRAINING_IMAGE_OPTIONS = [
  { value: 'no', label: 'No' },
  { value: 'yes', label: 'Yes' },
];

export const BATCH_SIZE_OPTIONS = [
  { name: '8', value: 8 },
  { name: '16', value: 16 },
  { name: '32', value: 32 },
];

export const LEARNING_RATE_SCHEDULER_OPTIONS = [
  { name: 'Constant Scheduler', value: 'constant_scheduler' },
  { name: 'Linear Scheduler', value: 'linear_scheduler' },
  { name: 'Cosine Scheduler', value: 'cosine_scheduler' },
];

export const MONITORED_METRIC_OPTIONS = [
  { name: 'Accuracy', value: 'accuracy' },
  { name: 'F1 Score', value: 'f1_score' },
  { name: 'Precision', value: 'precision' },
  { name: 'Recall', value: 'recall' },
];

export const PROBABILITIES = { cnn: '0.5', vit: '0.85' };

export const NO_DEFECT_ZERO_HOT_ENCODING_OPTIONS = [
  { value: false, label: 'No' },
  { value: true, label: 'Yes' },
];
